import React, { useCallback, useContext, useEffect, useState } from "react";
import Header from "../../Components/Header/Header";
import SwitchBox from "../../Components/FormControl/SwitchBox/SwitchBox";
import ConnectPayments from "../../Components/Settings/ConnectPayments";
import { AppContext } from "../../Context/AppContext";
import { MODULES } from "../../Helpers/Constants";
import { getAccessByModuleName } from "../../Routes/Routes";
import {
  getPaymentAccount,
  updatePaymentWallets,
} from "../../api/paymentAccount";
import styles from "./index.module.scss";

// Owner-facing wallet onboarding. Store Setting needs the "Store Setting"
// permission, which owners do not have, so this page carries the Connect flow
// under the "Payment Account" permission instead.
const Payments = () => {
  const { modules, showToastMessage } = useContext(AppContext);
  const [access, setAccess] = useState(null);
  const [stores, setStores] = useState([]);
  // Keyed by store id. Flags come from the login store list, account state from the API.
  const [flags, setFlags] = useState({});
  const [accounts, setAccounts] = useState({});
  const [saving, setSaving] = useState("");

  useEffect(() => {
    const moduleAccess = getAccessByModuleName(modules, MODULES.PAYMENT_ACCOUNT);
    setAccess(moduleAccess?.permissions ?? null);
    if (moduleAccess && !moduleAccess.permissions.read_access) {
      showToastMessage(undefined, "You are not authorized to access.", "error", false);
      window.location.href = "/dashboard";
    }
  }, [modules]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const assigned = JSON.parse(localStorage.getItem("assignStore") || "[]");
    setStores(assigned);
    setFlags(
      Object.fromEntries(
        assigned.map((store) => [
          store.id,
          {
            apple_pay_enabled: Boolean(store.apple_pay_enabled),
            google_pay_enabled: Boolean(store.google_pay_enabled),
          },
        ])
      )
    );
  }, []);

  const loadAccounts = useCallback(async () => {
    const entries = await Promise.all(
      stores.map(async (store) => {
        const response = await getPaymentAccount(store.id);
        return [store.id, response?.status === 200 ? response.data.data : null];
      })
    );
    setAccounts(Object.fromEntries(entries));
    // The account response is the source of truth once it carries the flags.
    setFlags((prev) => {
      const next = { ...prev };
      for (const [id, account] of Object.entries(Object.fromEntries(entries))) {
        if (account?.apple_pay_enabled !== undefined) {
          next[id] = {
            apple_pay_enabled: Boolean(account.apple_pay_enabled),
            google_pay_enabled: Boolean(account.google_pay_enabled),
          };
        }
      }
      return next;
    });
  }, [stores]);

  useEffect(() => {
    if (stores.length) loadAccounts();
  }, [stores, loadAccounts]);

  const handleFlagChange = async (storeId, field, value) => {
    const current = flags[storeId] ?? {};
    const next = {
      apple_pay_enabled: current.apple_pay_enabled ? 1 : 0,
      google_pay_enabled: current.google_pay_enabled ? 1 : 0,
      [field]: value ? 1 : 0,
    };
    setSaving(storeId);
    const response = await updatePaymentWallets(storeId, next);
    setSaving("");
    if (response?.status === 200) {
      setFlags((prev) => ({ ...prev, [storeId]: response.data.data }));
      showToastMessage(undefined, response.data.message, "success", false);
    } else if (response?.data?.message) {
      showToastMessage(undefined, response.data.message, "error", false);
    }
  };

  if (!access?.read_access) return null;

  return (
    <React.Fragment>
      <Header title="Payments" />
      <div id="MainContent" className="main-content">
        <div className={styles.paymentsWrapper}>
          {stores.length === 0 && (
            <p>You are not assigned to any store. Ask a Super Admin to assign one.</p>
          )}
          {stores.map((store) => {
            const account = accounts[store.id];
            const storeFlags = flags[store.id] ?? {};
            // The API refuses an off -> on change until the account can take charges.
            const canTakeCharges = Boolean(account?.charges_enabled);
            return (
              <div key={store.id} className={styles.storeCard}>
                <h2 className={styles.storeTitle}>{store.title}</h2>
                <ConnectPayments
                  storeId={store.id}
                  account={account}
                  canConnect={Boolean(access?.write_access)}
                />
                <div className="row">
                  <div className="col-md-6">
                    <label className="mb-1">Apple Pay enabled</label>
                    <SwitchBox
                      value={Boolean(storeFlags.apple_pay_enabled)}
                      disabled={!canTakeCharges || saving === store.id}
                      onChange={(data) => handleFlagChange(store.id, "apple_pay_enabled", data)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="mb-1">Google Pay enabled</label>
                    <SwitchBox
                      value={Boolean(storeFlags.google_pay_enabled)}
                      disabled={!canTakeCharges || saving === store.id}
                      onChange={(data) => handleFlagChange(store.id, "google_pay_enabled", data)}
                    />
                  </div>
                </div>
                {!canTakeCharges && (
                  <p className="mb-1">Finish payment setup above to turn the wallet buttons on.</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </React.Fragment>
  );
};

export default Payments;
